// src\themes\tolica\components\SwiperList.tsx
'use client';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/scrollbar';
import Image from 'next/image';
import { Heading } from '@/components/ui/Heading';
import Link from 'next/link';
import { SwiperListProps } from '@/core/models/slider-model';
import { useDictionary } from '@/core/hooks/use-dictionary';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useId } from 'react';
import CustomButton from './CustomButton';
import FullWidth from '@/components/shared/FullWidth';

// وقتی آیتم‌ها کمن (مثلاً ۳-۴ تا)، دوست داریم عرض هر اسلاید بزرگ‌تر بشه و
// کل ردیف رو مساوی پر کنه، به‌جای اینکه اسلاید ثابت ۳۰۰px بمونه و یه فضای
// خالی/نامتقارن ته ردیف بمونه. قبلاً این حالت با یه CSS grid جدا (به‌جای
// سوییپر) پیاده می‌شد، ولی grid با auto-fit وقتی عرض صفحه/زوم کم می‌شد، به‌جای
// اسکرول شدن، آیتم‌ها رو می‌بُرد خط بعد (ظاهر خراب). به‌جاش از خودِ سوییپر با
// عرض داینامیک استفاده می‌کنیم: max(حداقل‌عرض, سهم مساوی از عرض ردیف) - وقتی
// جا هست عرض مساوی می‌گیره و پر می‌کنه، وقتی جا نیست (زوم بالا، صفحه‌ی باریک)
// به حداقل‌عرض می‌چسبه و به‌جای شکستن خط، سوییپر خودش اسکرول‌پذیرش می‌کنه.
const MAX_VISIBLE = 5;
const MIN_ITEM_WIDTH = 240;
const GAP = 16;

export default function SwiperList({ data, isHome = false }: SwiperListProps & { isHome?: boolean }) {
  const { dictionary } = useDictionary();

  const items = Array.isArray(data.items)
    ? data.items
    : Array.isArray(data.sliderItems)
      ? data.sliderItems
      : [];

  const visibleCount = Math.min(items.length, MAX_VISIBLE) || 1;
  const slideWidth = `max(${MIN_ITEM_WIDTH}px, calc((100% - ${(visibleCount - 1) * GAP}px) / ${visibleCount}))`;

  const uid = useId().replace(/:/g, "-");

  const prevClass = `swiper-prev-${uid}`;
  const nextClass = `swiper-next-${uid}`;

  const renderCard = (item: (typeof items)[number]) => (
    <div className='relative overflow-hidden group'>
      <div
        className="w-full h-full mb-2 relative aspect-3/4 overflow-hidden"
      >
        {(item.image?.url ||
          item.image_info?.url) && (
            <Image
              src={
                item.image?.url ||
                item.image_info?.url
              }
              alt={item.title || ''}
              fill
              className="object-cover transition duration-500 group-hover:scale-105"
            />
          )}
      </div>
      <div className="absolute inset-0 z-0" />
      <div className='absolute bottom-3 right-3 left-3'>
        <Heading className='text-primary-900' level={3}>{item.title}</Heading>
        <p className="text-primary-600">{item.description}</p>
      </div>
    </div>
  );

  const content = (
    <>
      <div className="flex justify-between items-center mb-4">
        <Heading level={2} className="pb-2">
          {data.title}
        </Heading>

        <div className="flex items-center gap-3">
          {/* مشاهده همه */}
          {data.link &&
            <Link href={data.link} className="text-xs text-primary-600 hover:underline whitespace-nowrap">
              {dictionary.ui.pages.seeAll}
            </Link>
          }
          {/* خط جداکننده */}
          <span className="w-px h-4 bg-primary-300" />

          {/* دکمه‌های ناوبری - وقتی همه‌ی آیتم‌ها تو صفحه جا می‌شن، خودِ
              سوییپر این دکمه‌ها رو غیرفعال/محو می‌کنه (کلاس disabled) */}
          <div className="flex gap-2">
            <CustomButton className={`${prevClass} w-8 h-8 rounded-full border border-primary-900 bg-white flex items-center justify-center`}>
              <ChevronRight className="w-4.5 h-4.5 stroke-1" />
            </CustomButton>
            <CustomButton className={`${nextClass} w-8 h-8 rounded-full border border-primary-900 bg-white flex items-center justify-center`}>
              <ChevronLeft className="w-4.5 h-4.5 stroke-1" />
            </CustomButton>
          </div>
        </div>
      </div>

      <Swiper
        modules={[Navigation]}
        spaceBetween={GAP}
        slidesPerView="auto"
        className="w-full"
        navigation={{
          prevEl: `.${prevClass}`,
          nextEl: `.${nextClass}`,
        }}
        style={{ paddingBottom: '50px' }}
      >
        {items.map((item, index) => (
          <SwiperSlide
            key={index}
            style={{ width: slideWidth, height: '360px' }}
          >
            {item.link ? <Link href={item.link}>{renderCard(item)}</Link> : renderCard(item)}
          </SwiperSlide>
        ))}
      </Swiper>
    </>
  );

  // isHome: چون صفحه‌ی اصلی برخلاف بقیه‌ی صفحات داخل یه Container محدود
  // رندر نمی‌شه، از FullWidth برای کشیدن بک‌گراند طوسی تا لبه‌ی واقعی صفحه
  // استفاده می‌کنیم و پدینگ هم‌تراز با بقیه‌ی ویجت‌ها رو داخلش برمی‌گردونیم.
  if (isHome) {
    return (
      <FullWidth className="col-span-12 bg-primary-50 py-4">
        <div className="px-4 sm:px-6 lg:px-12 xl:px-20">{content}</div>
      </FullWidth>
    );
  }

  return (
    <div className="w-full col-span-12 bg-primary-50 p-4">
      {content}
    </div>
  );
}